import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownUp,
  ArrowRightLeft,
  Check,
  Clock,
  Dumbbell,
  Play,
  RotateCcw,
  Target,
  Trophy,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { MODE_CONFIG } from '@/components/modeConfig';
import { Ornament } from '@/components/Ornament';
import { Button, Card } from '@/components/ui';
import { minutesLabel, t } from '@/i18n';
import { useEngineStore } from '@/hooks/useAudio';
import { useProgress } from '@/hooks/useProgress';
import { lastSession } from '@/training/lastSession';
import { formatTransitionKey } from '@/training/summary';
import './SessionCompletePage.css';

/** Four numbers, one win, one thing to work on, one next step. Nothing else. */
export function SessionCompletePage() {
  const navigate = useNavigate();
  const summary = useEngineStore(lastSession);
  const { recommendation } = useProgress();

  const praise = useMemo(() => {
    if (!summary) return 'session.praiseKeep' as const;
    if (summary.accuracy >= 85) return 'session.praise' as const;
    if (summary.accuracy >= 65) return 'session.praiseGood' as const;
    return 'session.praiseKeep' as const;
  }, [summary]);

  if (!summary) {
    return (
      <AppShell>
        <Card section className="progress__empty">
          <p className="progress__empty-title">{t('progress.empty')}</p>
          <Button block onClick={() => navigate('/training')}>
            {t('modes.title')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  const transitionCount = summary.transitions.length;

  return (
    <AppShell>
      <div className="complete__hero">
        {/* One silhouette, low and to the side: decoration never sits under
            the words the player is reading. */}
        <Ornament kind="guitar" position="left" size={110} />
        <span className="complete__check">
          <Check size={46} strokeWidth={3} aria-hidden="true" />
        </span>
        <h1 className="complete__title">{t('session.done')}</h1>
        <p className="complete__praise">{t(praise)}</p>
      </div>

      <Card padded={false} className="complete__stats">
        <StatTile
          icon={<Clock size={20} aria-hidden="true" />}
          tone="blue"
          label={t('session.time')}
          value={formatDuration(summary.durationSec)}
        />
        <StatTile
          icon={<ArrowRightLeft size={20} aria-hidden="true" />}
          tone="amber"
          label={t('session.transitions')}
          value={String(summary.mode === 'chordChange' ? transitionCount : summary.attempts)}
        />
        <StatTile
          icon={<Target size={20} aria-hidden="true" />}
          tone="green"
          label={summary.clarityScore !== null ? t('session.clarity') : t('session.accuracy')}
          value={`${summary.clarityScore ?? summary.accuracy}%`}
        />
        <StatTile
          icon={<ArrowDownUp size={20} aria-hidden="true" />}
          tone="violet"
          label={t('session.rhythm')}
          value={summary.rhythmScore === null ? '—' : `${summary.rhythmScore}%`}
        />
      </Card>

      <div className="complete__pair">
        <Card>
          <div className="complete__mini">
            <span className="home-stat__icon home-stat__icon--amber">
              <Trophy size={20} aria-hidden="true" />
            </span>
            <div>
              <p className="stat-label">{t('session.best')}</p>
              <p className="complete__mini-value complete__mini-value--accent">
                {summary.bestTransitionKey ? formatTransitionKey(summary.bestTransitionKey) : '—'}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="complete__mini">
            <span className="home-stat__icon home-stat__icon--green">
              <Dumbbell size={20} aria-hidden="true" />
            </span>
            <div>
              <p className="stat-label">{t('session.practiceNext')}</p>
              <p className="complete__mini-value complete__mini-value--green">
                {recommendation.transitionKey
                  ? formatTransitionKey(recommendation.transitionKey)
                  : (recommendation.chordIds.join(' → ') || t('modes.rhythm'))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="complete__next">
        <span className="home-stat__icon home-stat__icon--green">
          <Target size={20} aria-hidden="true" />
        </span>
        <div>
          <p className="complete__next-title">{t('session.nextSession')}</p>
          <p className="complete__next-value">
            {recommendation.chordIds.join(' → ') || t('modes.rhythm')}
            {recommendation.bpm > 0 && ` • ${recommendation.bpm} BPM`}
            {` • ${minutesLabel(recommendation.durationMin)}`}
          </p>
          <p className="complete__next-reason">{recommendation.explanationRu}</p>
        </div>
      </Card>

      <div className="active__actions">
        <Button variant="secondary" onClick={() => navigate(MODE_CONFIG[summary.mode].to)}>
          <RotateCcw size={18} aria-hidden="true" />
          {t('session.repeat')}
        </Button>
        <Button
          onClick={() =>
            navigate(MODE_CONFIG[recommendation.mode].to, { state: { recommendation } })
          }
        >
          <Play size={18} fill="currentColor" aria-hidden="true" />
          {t('session.continue')}
        </Button>
      </div>
    </AppShell>
  );
}

interface StatTileProps {
  icon: React.ReactNode;
  tone: 'blue' | 'amber' | 'green' | 'violet';
  label: string;
  value: string;
}

function StatTile({ icon, tone, label, value }: StatTileProps) {
  return (
    <div className="complete__tile">
      <span className={`complete__tile-icon complete__tile-icon--${tone}`}>{icon}</span>
      <span className="complete__tile-label">{label}</span>
      <span className={`complete__tile-value complete__tile-value--${tone} tnum`}>{value}</span>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}
